/**
 * Create the Lost Report Result Agent node
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';
import { searchFAQs, formatSearchResults } from '../../utils/faq-search';

export function createLostReportResultAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'lost_report_result_agent',
    name: 'Lost Report Result Agent',
    description: 'Step 4: Handle search results, show report or call SAAED',
    
    // Initial prompt to trigger AI behavior

    
    // Backend provides the actual system instruction via ephemeral token

    
    // Initial prompt to present results
    initialPrompt: 'The search is complete. Check the SEARCH RESULTS DATA section in your system instructions above. The count is explicitly shown there. If count = 1, announce the report details from the data provided. If count = 0, say no reports found and direct to call SAAED. If count > 1, DO NOT read any report numbers - just say there are multiple reports and direct user to call SAAED office at 800-72233.',
    
    entryGuardrails: requireLanguage(),
    
    exitGuardrails: {
      transitions: new Map([
        ['new_search', {
          target: { agentId: 'lost_report_upload_extract_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['start_renewal', {
          target: { agentId: 'renewal_input_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['try_different_dates', {
          target: { agentId: 'lost_report_search_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['finish', {
          target: { agentId: 'router_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
      ]),
      fallback: { agentId: 'router_agent' },
    },
    
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {
      if (toolName === 'new_search') {
        console.log('[LostReportResultAgent] Starting new search');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Starting new search' },
          triggersTransition: true,
          transitionTo: 'lost_report_upload_extract_agent',
          transitionData: {
            licenseData: undefined,
            searchResults: undefined,
          },
        };
      }
      
      if (toolName === 'start_renewal') {
        const reportNumber = args.report_number;
        console.log('[LostReportResultAgent] Starting renewal for report:', reportNumber);
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Starting renewal process' },
          triggersTransition: true,
          transitionTo: 'renewal_input_agent',
          transitionData: {
            prefilledReportNumber: reportNumber,
            licenseData: undefined,
            searchResults: undefined,
          },
        };
      }
      
      if (toolName === 'try_different_dates') {
        console.log('[LostReportResultAgent] Trying different dates');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Returning to date picker' },
          triggersTransition: true,
          transitionTo: 'lost_report_search_agent',
          transitionData: {
            searchResults: undefined,
          },
        };
      }
      
      if (toolName === 'finish') {
        console.log('[LostReportResultAgent] Finishing lost report flow');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Returning to menu' },
          triggersTransition: true,
          transitionTo: 'router_agent',
          transitionData: {
            licenseData: undefined,
            searchResults: undefined,
          },
        };
      }
      
      // Cross-flow navigation - Accident reporting
      if (toolName === 'start_accident_reporting') {
        console.log('[LostReportResultAgent] Cross-flow: Starting accident reporting');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Starting accident reporting' },
          triggersTransition: true,
          transitionTo: 'accident_start_agent',
          transitionData: {
            licenseData: undefined,
            searchResults: undefined,
          },
        };
      }
      
      // Cross-flow navigation - FAQ
      if (toolName === 'go_to_faq') {
        console.log('[LostReportResultAgent] Cross-flow: Going to FAQ');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Going to FAQ' },
          triggersTransition: true,
          transitionTo: 'faq_agent',
          transitionData: {
            licenseData: undefined,
            searchResults: undefined,
          },
        };
      }
      
      // Inline FAQ
      if (toolName === 'search_faq') {
        const question = args.question || '';
        const searchResults = await searchFAQs(question, 3, 2);
        const language = context.transitionData.language || 'en';
        const formattedResults = formatSearchResults(searchResults, language);
        
        console.log('[LostReportResultAgent] Inline FAQ:', question);
        
        return {
          toolName,
          args,
          result: {
            success: true,
            results_count: searchResults.length,
            results: formattedResults,
            message: 'Answer the question, then remind user of their options.',
          },
          triggersTransition: false,
        };
      }
      
      return {
        toolName,
        args,
        result: { success: false, error: 'Unknown tool' },
      };
    },
    
    onActivate: async (context: AgentContext) => {
      const results = context.transitionData.searchResults;
      console.log('[LostReportResultAgent] Activated - Final step of lost report flow');
      console.log('  Search results:', results);
    },
    
    onDeactivate: async (context: AgentContext) => {
      console.log('[LostReportResultAgent] Deactivated');
    },
  };
}

export default createLostReportResultAgent;

